import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Plus, BarChart2, Users, Upload, ChevronRight, ChevronDown,
  CheckCircle, AlertCircle, Trash2, Loader, User
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { listCourses, getCourseAnalytics, deleteCourse } from '../../services/studioApi';
import { authService } from '../../services/authService';
import UnifiedHeader from '../../components/UnifiedHeader';
import RightSideMenu from '../../components/RightSideMenu';
import SubscriptionModal from '../../components/SubscriptionModal';
import { AssignmentManagerTab } from './AssignmentManager';
import { ClassManagementTab } from './ClassManagement';
import { StudentRosterTab } from './StudentRoster';
import './Studio.css';

function StatusBadge({ status, tT }) {
  const map = {
    ready: { cls: 'badge-green', label: tT?.statusReady || 'Ready' },
    processing: { cls: 'badge-orange', label: tT?.statusProcessing || 'Processing' },
    error: { cls: 'badge-red', label: tT?.statusError || 'Error' },
  };
  const { cls, label } = map[status] || { cls: 'badge-grey', label: status };
  return <span className={`badge ${cls}`}>{label}</span>;
}

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tTeacher = t.teacher || {};

  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'courses');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [showReactions, setShowReactions] = useState(false);
  const [showRightMenu, setShowRightMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // course to delete
  const [deleting, setDeleting] = useState(false);
  const user = authService.getUser();

  const handleOpenSubscription = () => { setShowSubscriptionModal(true); document.body.classList.add('modal-open'); };
  const handleCloseSubscription = () => { setShowSubscriptionModal(false); document.body.classList.remove('modal-open'); };

  const handleDeleteCourse = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await deleteCourse(deleteConfirm.id);
      setDeleteConfirm(null);
      loadCourses();
    } catch (e) {
      alert('Failed to delete course. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCourses();
      setCourses(data);
      if (data.length > 0 && !selectedCourseId) {
        setSelectedCourseId(data[0].id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedCourseId]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  useEffect(() => {
    if (tab === 'analytics' && selectedCourseId) {
      getCourseAnalytics(selectedCourseId)
        .then(setAnalyticsData)
        .catch(console.error);
    }
  }, [tab, selectedCourseId]);

  const masteryColor = (score) => {
    if (score === 0) return 'heatmap-cell-0';
    if (score < 0.4) return 'heatmap-cell-low';
    if (score < 0.7) return 'heatmap-cell-mid';
    if (score < 0.85) return 'heatmap-cell-high';
    return 'heatmap-cell-mastered';
  };

  return (
    <div className="studio-page">
      <UnifiedHeader onOpenMenu={() => setShowRightMenu(true)} />

      <div className="studio-main">
        <div className="studio-tabs">
          <button className={`studio-tab ${tab === 'courses' ? 'active' : ''}`} onClick={() => setTab('courses')}>
            <BookOpen size={16} />
            {tTeacher.myCourses || 'My Courses'}
          </button>
          <button className={`studio-tab ${tab === 'assignments' ? 'active' : ''}`} onClick={() => setTab('assignments')}>
            <CheckCircle size={16} />
            {tTeacher.assignments || 'Assignments'}
          </button>
          <button className={`studio-tab ${tab === 'classes' ? 'active' : ''}`} onClick={() => setTab('classes')}>
            <Users size={16} />
            {tTeacher.classes || 'Classes'}
          </button>
          <button className={`studio-tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
            <BarChart2 size={16} />
            {tTeacher.analytics || 'Analytics'}
          </button>
          <button className={`studio-tab ${tab === 'students' ? 'active' : ''}`} onClick={() => setTab('students')}>
            <User size={16} />
            {tTeacher.students || 'Students'}
          </button>
        </div>

        {(tab === 'courses' || tab === 'analytics') && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button className="btn-primary" onClick={() => navigate('/studio/teacher/upload')}>
              <Upload size={15} />
              {tTeacher.uploadSyllabus || 'Upload Syllabus'}
            </button>
          </div>
        )}

        {/* ── MY COURSES ── */}
        {tab === 'courses' && (
          <>
            {loading ? (
              <div className="loading-center"><div className="spinner" /></div>
            ) : courses.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><BookOpen size={48} /></div>
                <h3>{tTeacher.noCoursesTeacherTitle || tTeacher.noCoursesTitle || 'No courses yet'}</h3>
                <p>{tTeacher.noCoursesTeacherDesc || 'Upload a syllabus PDF to create your first course'}</p>
                <button className="btn-primary" onClick={() => navigate('/studio/teacher/upload')}>
                  <Upload size={16} />
                  {tTeacher.uploadSyllabus || 'Upload Syllabus'}
                </button>
              </div>
            ) : (
              <div className="studio-card-grid">
                {courses.map(course => (
                  <div key={course.id} className="studio-card" style={{ cursor: 'pointer', position: 'relative' }}
                    onClick={() => navigate(`/studio/teacher/courses/${course.id}`)}>
                    {/* Delete button */}
                    <button
                      onClick={e => { e.stopPropagation(); setDeleteConfirm(course); }}
                      title="Delete course"
                      style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: '#ddd', padding: '0.2rem', borderRadius: 6, display: 'flex', alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                      onMouseLeave={e => e.currentTarget.style.color = '#ddd'}>
                      <Trash2 size={15} />
                    </button>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', paddingRight: '1.5rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', flex: 1 }}>
                        {course.title}
                      </h3>
                      <StatusBadge status={course.status} tT={tTeacher} />
                    </div>
                    {course.description && (
                      <p style={{ margin: '0 0 1rem', color: '#666', fontSize: '0.85rem', lineHeight: 1.4 }}>
                        {course.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>
                      <span><BookOpen size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />{course.concept_count || 0} {tTeacher.concepts || 'concepts'}</span>
                      <span><Users size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />{course.student_count || 0} {tTeacher.students || 'students'}</span>
                    </div>
                    <button className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
                      {tTeacher.openCourse || 'Open Course'} <ChevronRight size={14} />
                    </button>
                  </div>
                ))}

                {/* Add course card */}
                <div className="studio-card" style={{
                  cursor: 'pointer', border: '2px dashed #c5d0e8',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', minHeight: 160, gap: '0.75rem',
                  background: '#f8f9ff'
                }} onClick={() => navigate('/studio/teacher/upload')}>
                  <Plus size={28} color="#5B35A0" />
                  <span style={{ color: '#5B35A0', fontWeight: 600, fontSize: '0.9rem' }}>
                    {tTeacher.addCourse || 'Add New Course'}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── ASSIGNMENTS ── */}
        {tab === 'assignments' && <AssignmentManagerTab />}

        {/* ── CLASSES ── */}
        {tab === 'classes' && <ClassManagementTab />}

        {/* ── STUDENTS ── */}
        {tab === 'students' && <StudentRosterTab />}

        {/* ── ANALYTICS ── */}
        {tab === 'analytics' && (
          <div>
            <div className="section-header" style={{ marginBottom: '1.5rem' }}>
              <h2><BarChart2 size={20} />{tTeacher.analytics || 'Analytics'}</h2>
              <select
                className="form-select"
                style={{ width: 'auto', minWidth: 200 }}
                value={selectedCourseId}
                onChange={e => setSelectedCourseId(e.target.value)}
              >
                {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>

            {!analyticsData ? (
              <div className="loading-center"><div className="spinner" /></div>
            ) : (
              <>
                {/* Heatmap */}
                {analyticsData.heatmap.length === 0 ? (
                  <div className="studio-card">
                    <p style={{ color: '#888', textAlign: 'center', padding: '2rem 0' }}>
                      {tTeacher.noStudentsYet || 'No students enrolled yet'}
                    </p>
                  </div>
                ) : (
                  <div className="studio-card" style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: '0 0 1.25rem', fontSize: '0.95rem', fontWeight: 700, color: '#555' }}>
                      {tTeacher.masteryHeatmap || 'Student × Concept Mastery'}
                    </h3>
                    <table className="heatmap-table">
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', minWidth: 100 }}>{tTeacher.student || 'Student'}</th>
                          {analyticsData.concepts.map(c => (
                            <th key={c.id} title={c.title} style={{ maxWidth: 80 }}>
                              {c.title.length > 12 ? c.title.slice(0, 12) + '…' : c.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.heatmap.map(row => (
                          <tr key={row.student_id}>
                            <td style={{ textAlign: 'left', fontWeight: 500, paddingRight: '0.5rem' }}>
                              {row.student_name}
                            </td>
                            {analyticsData.concepts.map(c => {
                              const data = row.concepts[c.id] || { mastery: 0 };
                              return (
                                <td key={c.id}>
                                  <span className={`heatmap-cell ${masteryColor(data.mastery)}`}>
                                    {data.mastery > 0 ? Math.round(data.mastery * 100) + '%' : '–'}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Video comprehension reactions per concept — collapsible */}
                {analyticsData.concepts.some(c => Object.keys(c.video_reactions || {}).length > 0) && (
                  <div className="studio-card" style={{ marginBottom: '1.5rem' }}>
                    <button
                      onClick={() => setShowReactions(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                    >
                      <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#555', flex: 1 }}>
                        🎯 {tTeacher.videoComprehension || 'Video Comprehension Ratings'}
                      </h3>
                      <ChevronDown size={16} color="#999" style={{ transform: showReactions ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>
                    {showReactions && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                        {analyticsData.concepts.filter(c => Object.keys(c.video_reactions || {}).length > 0).map(c => {
                          const r = c.video_reactions || {};
                          const total = (r.great||0) + (r.good||0) + (r.confused||0) + (r.lost||0);
                          return (
                            <div key={c.id} style={{ padding: '0.75rem', background: '#f8f9ff', borderRadius: 8 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.5rem' }}>{c.title}</div>
                              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                {[
                                  { key: 'great',   emoji: '😄', color: '#22c55e', label: tTeacher.reactionGotIt || 'Got it' },
                                  { key: 'good',    emoji: '🙂', color: '#eab308', label: tTeacher.reactionMostly || 'Mostly' },
                                  { key: 'confused',emoji: '😕', color: '#f97316', label: tTeacher.reactionNotQuite || 'Not quite' },
                                  { key: 'lost',    emoji: '😟', color: '#ef4444', label: tTeacher.reactionLost || 'Lost' },
                                ].map(({ key, emoji, color, label }) => (
                                  <span key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.82rem' }}>
                                    <span style={{
                                      width: 28, height: 28, borderRadius: '50%',
                                      background: `color-mix(in srgb, ${color} 20%, white)`,
                                      border: `2px solid ${color}`,
                                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: '0.9rem'
                                    }}>{emoji}</span>
                                    <strong>{r[key] || 0}</strong>
                                    <span style={{ color: '#888' }}>{label}</span>
                                  </span>
                                ))}
                                <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#aaa' }}>{total} {total !== 1 ? (tTeacher.ratings || 'ratings') : (tTeacher.rating || 'rating')}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Struggling students */}
                {analyticsData.struggling_students.length > 0 && (
                  <div className="studio-card">
                    <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: '#e53935', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <AlertCircle size={16} />
                      {tTeacher.strugglingStudents || tTeacher.struggling || 'Students Needing Attention'}
                    </h3>
                    {analyticsData.struggling_students.map(s => (
                      <div key={s.student_id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>{s.student_name}</div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {s.weak_concepts.map(wc => (
                            <span key={wc.concept_id} className="badge badge-red">
                              {wc.concept_title}: {Math.round(wc.mastery * 100)}%
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <RightSideMenu
        isOpen={showRightMenu}
        onClose={() => setShowRightMenu(false)}
        user={user}
        currentPlan="free"
        onOpenSubscription={handleOpenSubscription}
      />
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={handleCloseSubscription}
        currentPlan="free"
      />

      {/* Course delete confirmation modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '1.5rem', maxWidth: 400, width: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
              <Trash2 size={18} color="#ef4444" />
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}>Delete "{deleteConfirm.title}"?</span>
            </div>
            <p style={{ color: '#555', fontSize: '0.88rem', margin: '0 0 0.5rem', lineHeight: 1.6 }}>
              This will permanently delete the course, all concepts, questions, assignments, and syllabus content.
            </p>
            <p style={{ color: '#5B35A0', fontSize: '0.85rem', margin: '0 0 1.25rem', fontWeight: 500 }}>
              Generated videos are kept — you can find them in My Videos.
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ padding: '0.5rem 1.1rem', borderRadius: 8, border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer', fontSize: '0.88rem' }}>
                Cancel
              </button>
              <button onClick={handleDeleteCourse} disabled={deleting}
                style={{ padding: '0.5rem 1.1rem', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {deleting && <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                Delete Course
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
